<template>
  <search-provider
    :placename="placename"
    :selected-category-ids="categoryIds"
    :data-lang="lang"
    :class="{ desktop: $isDesktop }"
    :show-default-heading="hasQueryParams"
  >
    <template
      v-if="lang === 'en-gb' && Object.keys(route.query).length === 0"
      #before-summary
    >
      <section class="glamping">
        <h1>Glamping sites</h1>
        <p>
          Glamping – what's it all about? Well, essentially, it's like camping,
          but someone sorts the accommodation out for you – be that a tent, a
          <nuxt-link to="/campsites/-/camping-pods/">
            <span>camping pod</span> </nuxt-link
          >, a yurt, a
          <nuxt-link to="/glamping/-/shepherds-huts/">
            <span>shepherd's hut</span> </nuxt-link
          >, a treehouse&hellip; Plenty of folk say that it's the best of both
          worlds, mixing up the best bits of the outdoorsy life (the fresh air,
          the stargazing, the country views) with the comfort that comes from
          knowing you'll have a roof of some description over your head once it
          comes to bedtime. So if you fancy an evening by the campfire but don't
          want to give up the idea of a comfy bed, check out our best
          glampsites!
        </p>
        <lazy-glamping-popular-destinations />
        <lazy-glamping-popular-searches />
      </section>
    </template>

    <template
      v-if="lang === 'en-gb' && Object.keys(route.query).length === 0"
      #popular-locations
    >
      &#8203;
      <!-- making it empty -->
    </template>

    <template
      v-if="lang === 'en-gb' && Object.keys(route.query).length === 0"
      #search-pagepart
    >
      &#8203;
      <!-- making it empty -->
    </template>

    <template
      v-if="lang === 'en-gb' && Object.keys(route.query).length === 0"
      #search-guides
    >
      <lazy-glamping-guides />
    </template>

    <template
      v-if="
        lang === 'en-gb' && $isDesktop && Object.keys(route.query).length === 0
      "
      #lower-pagepart
    >
      <section class="glamping">
        <h3>Picking your perfect site</h3>
        <p>
          Where your glamping style fits on the glam-o-meter is up to you: we've
          got everything from properly luxurious
          <a href="/glamping/-/hot-tub/">glamping with hot tubs</a> (for those
          special weekends away with your beloved) to simple wooden camping pods
          (when you just need somewhere warm to hunker down for the night). For
          a truly memorable stay you might fancy a spot of glamping in a yurt,
          sorting a stay in a treehouse or cosying up in a shepherd's hut.
        </p>
        <p>
          Is there such a thing as family glamping? Of course! It's a fab idea
          for a family holiday, often coming with the bonus of plenty of space
          for the kids to run about and abandon screen time in favour of
          marshmallows and mud pies. A shepherd's hut might be a tad too cosy
          for your brood, but there are plenty of places around where you can
          stay in a <a href="/glamping/-/safari-tents/">safari tent</a> or
          <a href="/glamping/-/bell-tents/">bell tent</a>, or sort a lodge
          holiday.
        </p>
        <p>
          If family extends to four-legged members too, take a look at the
          options for
          <a href="/glamping/-/dogs-allowed/">glamping with dogs</a> too –
          they'll no doubt be delighted to have somewhere comfy to cuddle up
          between country walkies.
        </p>
        <h3>How does it work?</h3>
        <p>
          One of the truly nifty things about glamping is that is really cuts
          down on the amount of kit you need to bring – fab news for those who
          want to travel light, don't want to invest in a whole heap of camping
          paraphernalia or have a car that's already stuffed full of beach kit,
          bikes and other holiday essentials.
        </p>
        <p>
          The exact details will vary from site to site, of course, but you can
          certainly count on being able to turn up without a tent or a sleeping
          mat. In camping pods you'll most likely need to bring a sleeping bag
          and pillow; in other places you're likely to find bedding and towels
          provided. At smarter places you can also expect to find such things as
          firepits, fully-equipped kitchens and ensuite bathrooms; you might
          even want to indulge yourself by glamping with a hot tub (you've
          earned it, right?)
        </p>
        <p>
          Finding out exactly what's in your chosen glamping spot is a cinch –
          each of our listings is designed to give you as much info as possible
          about what's included, so be sure to check the details before you set
          off to make sure you don't leave any vital components behind.. In many
          cases, you'll find that the majority of stuff you need is supplied,
          making getting out to the country life a simple case of chucking a
          toothbrush, some walking boots and a couple of changes of clothes in
          your bag and hopping on a train (although possibly not if you're
          bringing the kids…).
        </p>
        <h3>What about food?</h3>
        <p>
          A vital part of any holiday, and a glamping break is no different. If
          you're going for the full-on fresh-air life, pick a place that's got a
          barbecue or one that allows campfires so you can burn cook your dinner
          over flames in the glorious open air. (Top tip: these are even better
          if you can find a local farm shop to supply the goodies – and you get
          a warm glow from supporting local business too).
        </p>
        <p>
          But let's face it, there may occasionally be times when the weather
          doesn't give its blessing to such endeavours. This, friends, is where
          glamping really comes into its own. In many cases, you'll find your
          chosen place has a kitchen of some description - be it a properly
          swish number with all mod cons or just a little corner with a
          microwave and kettle. Whether it's a full meal created in a lodge
          kitchen or just a cuppa served out on the steps of your shepherd's
          hut, it's bound to be extra tasty because of that good dollop of fresh
          air.
        </p>
        <p>
          Of course, you may also wish to say a massive 'nope, ta' to the idea
          of cooking and other associated chores. And why not? Handily plenty of
          places offer glamping near a pub – or you could go the whole hog and
          stay in a pub's own glamping site for the ultimate in short staggers
          back to your pad.
        </p>
        <h3>Where can I go glamping?</h3>
        <p>
          The possibilities are endless… and the options are growing all the
          time. Given the, ahem, 'changeable' climate, glamping in the UK is a
          hugely popular choice. Farm stays, seaside stays, forest retreats –
          all with the promise of a comfy bed and protection from the elements?
          Sounds like a winner. Check out some of our choices for
          <a href="/glamping/England/">glamping in England</a>,
          <a href="/glamping/Wales/">glamping in Wales</a>,
          <a href="/glamping/Scotland/">glamping in Scotland</a> and
          <a href="/campsites/Northern_Ireland/"
            >glamping in Northern Ireland</a
          >
          here.
        </p>
        <p>
          Beyond that? Well, the world's your (comfy, cosy) oyster. Why not
          spend your summer hols
          <a href="/glamping/france/">glamping in France</a> or
          <a href="/glamping/spain/">glamping in Spain</a>? Or take your holiday
          in <a href="/glamping/croatia/">Croatia</a>,
          <a href="/glamping/portugal/">Portugal</a>,
          <a href="/glamping/colombia/">Colombia</a>,
          <a href="/glamping/south-africa/">South Africa</a>… seriously, there's
          plenty to pick from.
        </p>
        <h3>What about glamping in winter?</h3>
        <p>
          Sure, why not – we all need a fix of fresh air and nature even in the
          depths of winter, eh? Lots of our glamping sites are open all year,
          with handy features like woodburning stoves and underfloor heating to
          make sure you stay nice and toasty.
        </p>
      </section>
    </template>
    <template #pagination-controls="{ paginationData, searchFilters }">
      <lazy-search-results-pagination-controls
        :pagination-data="paginationData"
        :search-filters="searchFilters"
        :use-current-route="true"
      />
    </template>
  </search-provider>
</template>

<script setup lang="ts">
import { useNuxtApp, useRoute, createError } from 'nuxt/app'
import { computed, onMounted } from 'vue'
import { httpHeadersCache } from '../../apps/search/helpers'
import { isSearchUrlRoute } from '../../apps/search/isSearchUrlComponent'
import { ensureArray } from '../../utility/array'
import { GLAMPING_CATEGORIES } from '../../utility/glamping'

// Page metadata
definePageMeta({
  key: 'Glamping2Page',
  // Removed layout: false to use default layout at build time instead of runtime
  validate: (route) => isSearchUrlRoute(route),
  httpHeaders: (to) => httpHeadersCache(to),
  meta: {
    type: 'glamping',
    category: 'glamping',
    app: 'search',
  },
})

const route = useRoute()
const { $gtm, $isDesktop } = useNuxtApp()

// Constants
const categoryIds = GLAMPING_CATEGORIES
const slug = 'glamping'

if (!isSearchUrlRoute(route)) {
  throw createError({
    statusCode: 404,
    statusMessage: `Page Not Found2: ${route.path}`,
    fatal: process.client,
  })
}

// Computed
const lang = computed(() => route.params.lang || 'en-gb')

const placename = computed(
  () => ensureArray(route.query.placename)[0] || undefined,
)

const hasQueryParams = computed(() => Object.keys(route.query).length > 0)

const category = computed(() => ({
  categoryId: categoryIds.join(','),
  rootSlug: slug,
}))

// Lifecycle
onMounted(() => {
  $gtm?.push({
    pageCategories: ['glamping'].join(','),
  })
})

// Watchers
watch(
  () => category.value,
  () => {
    void nextTick(() => {
      $gtm?.push({
        pageCategories: category.value.rootSlug,
      })
    })
  },
  { immediate: true },
)
</script>

<style scoped>
.glamping {
  background: white;
  width: 100vw;
  padding: 1em 1.4em 1.4em;
  border-top: 1px solid #dedede;
}

p {
  font-size: var(--body);
  line-height: 1.4;
  margin-bottom: 1.25em;
}

.glamping :deep(h1),
.glamping :deep(h2) {
  color: var(--colour-dark-blue);
  font-size: 1.8em;
  font-weight: bold;
  margin: 0;
}

.glamping :deep(h1) {
  margin: 0 0 0.25em 0;
}

.glamping :deep(h2) {
  font-size: 1.35em;
}

.glamping :deep(h3) {
  color: var(--colour-dark-blue);
}

.glamping :deep(.scrollable) {
  overflow-x: auto;
  overflow-y: visible;
  padding: 1em 1.4em;
  scroll-padding: 0 1.4em;
  scroll-snap-type: x mandatory;
}

.glamping :deep(.horizontal-slider) {
  margin: auto -1.4em;
  grid-template-columns: repeat(11, 34vw) !important;
}

.glamping :deep(.horizontal-slider.popular-searches a) {
  order: unset !important;
}

@supports (scrollbar-width: none) {
  .glamping :deep(.scrollable) {
    scrollbar-width: none;
  }

  .desktop .glamping :deep(.scrollable) {
    scrollbar-width: thin;
  }
}

@supports selector(: : -webkit-scrollbar) {
  .glamping :deep(.scrollable:not(.desktop)::-webkit-scrollbar) {
    display: none;
  }
}

.desktop .glamping {
  width: auto;
  padding-left: var(--page-width-padding);
}

.desktop p {
  font-size: inherit;
  line-height: 1.5;
  margin-bottom: 1.5em;
}

.desktop :deep(.horizontal-slider.popular-searches),
.desktop :deep(.horizontal-slider.top-destinations) {
  grid-template-columns: repeat(11, 14em) !important;
  margin: auto calc(var(--page-width-padding) * -1);
  padding: 1em var(--page-width-padding);
  scroll-padding: 0 var(--page-width-padding);
}
</style>
